import { useParams, Link } from "react-router";
import { useMemo } from "react";
import { ExternalLink } from "lucide-react";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { TableSkeleton } from "@/components/shared/loading-skeleton";
import {
  useGetApiChannelConnections,
} from "@/lib/api/generated/channels/channels";
import {
  useGetApiChannelDeployments,
} from "@/lib/api/generated/channels/channels";
import { useTenantContext } from "@/lib/hooks/use-tenant-context";
import type {
  ChannelConnectionRecord,
  ChannelDeploymentRecord,
} from "../../../../../packages/shared-types/src/index.js";
import { AgentTabSurface } from "./agent-tab-surface";

export function AgentDeploymentsTab() {
  const { agentId } = useParams<{ agentId: string }>();
  const { tenantId } = useTenantContext();
  const { data: allDeployments, isLoading: deploymentsLoading } =
    useGetApiChannelDeployments<ChannelDeploymentRecord[]>({
      query: { enabled: Boolean(tenantId) },
    });
  const { data: connections, isLoading: connectionsLoading } =
    useGetApiChannelConnections<ChannelConnectionRecord[]>({
      query: { enabled: Boolean(tenantId) },
    });

  const isLoading = deploymentsLoading || connectionsLoading;

  // Filter deployments for this agent
  const deployments = useMemo(
    () => allDeployments?.filter((d) => d.agentId === agentId) ?? [],
    [allDeployments, agentId],
  );

  // Build a lookup for connection display names
  const connectionNames = useMemo(() => {
    const map = new Map<string, string>();
    if (connections) {
      for (const conn of connections) {
        map.set(conn.id, conn.displayName);
      }
    }
    return map;
  }, [connections]);

  if (isLoading) {
    return <TableSkeleton rows={3} cols={5} />;
  }

  if (deployments.length === 0) {
    return (
      <EmptyState
        title="No deployments"
        description="This agent has no channel deployments. Deploy it from the deployments page."
        action={{
          label: "Go to Deployments",
          onClick: () => {
            window.location.href = "/workspace/deployments";
          },
        }}
      />
    );
  }

  return (
    <AgentTabSurface
      title="Channel Deployments"
      description="Where this agent is exposed across tenant channels."
      actions={
        <Link
          to="/workspace/deployments"
          className="inline-flex items-center gap-1 text-xs font-medium"
          style={{ color: "var(--accent)" }}
        >
          Manage Deployments
          <ExternalLink size={12} />
        </Link>
      }
    >
      <div className="overflow-hidden bg-background">
        <table className="w-full text-xs">
          <thead>
            <tr style={{ background: "var(--bg-accent)" }}>
              <th
                className="text-left px-4 py-2 font-medium"
                style={{ color: "var(--muted-foreground)" }}
              >
                Connection
              </th>
              <th
                className="text-left px-4 py-2 font-medium"
                style={{ color: "var(--muted-foreground)" }}
              >
                Status
              </th>
              <th
                className="text-center px-4 py-2 font-medium"
                style={{ color: "var(--muted-foreground)" }}
              >
                DMs
              </th>
              <th
                className="text-center px-4 py-2 font-medium"
                style={{ color: "var(--muted-foreground)" }}
              >
                Groups
              </th>
              <th
                className="text-center px-4 py-2 font-medium"
                style={{ color: "var(--muted-foreground)" }}
              >
                Mention Required
              </th>
            </tr>
          </thead>
          <tbody>
            {deployments.map((dep) => (
              <tr key={dep.id} className="odd:bg-background even:bg-[var(--bg-subtle)]">
                <td className="px-4 py-2.5 font-medium" style={{ color: "var(--text-strong)" }}>
                  {connectionNames.get(dep.channelConnectionId) ?? (
                    <span className="font-mono" style={{ color: "var(--muted-foreground)" }}>
                      {dep.channelConnectionId}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <StatusBadge status={dep.status} />
                </td>
                <td className="px-4 py-2.5 text-center">
                  <BoolDot value={dep.allowDms} />
                </td>
                <td className="px-4 py-2.5 text-center">
                  <BoolDot value={dep.allowGroups} />
                </td>
                <td className="px-4 py-2.5 text-center">
                  <BoolDot value={dep.requireMention} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AgentTabSurface>
  );
}

/** Small visual indicator for boolean fields */
function BoolDot({ value }: { value: boolean }) {
  return (
    <span
      className="inline-block w-2 h-2 rounded-full"
      style={{ background: value ? "var(--accent)" : "var(--border)" }}
      title={value ? "Yes" : "No"}
    />
  );
}
